import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

const api = apiService();

// ————————————————————————————————
// Async thunks for Users
// ————————————————————————————————

export const fetchProfile = createAsyncThunk(
  'users/fetchProfile',
  async () => {
    const res = await api.get('/user');
    return res.data;
  }
);

export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (userId: string) => {
    const res = await api.get(`/user/${userId}`);
    return res.data;
  }
);

export const fetchUserByAdmin = createAsyncThunk(
  'users/fetchUserByAdmin',
  async (userId: string) => {
    const res = await api.get(`/user/admin/${userId}`);
    return res.data;
  }
);

export const fetchFullUser = createAsyncThunk(
  'users/fetchFullUser',
  async (userId: string) => {
    const res = await api.get(`/user/full/${userId}`);
    return res.data;
  }
);

export const fetchAllUsersAdmin = createAsyncThunk(
  'users/fetchAllUsersAdmin',
  async (
    params: {
      page?: number;
      limit?: number;
      status?: string;
      name?: string;
      gender?: string;
      // ADMIN-SIGNUPS-V1: join-date range (YYYY-MM-DD, inclusive) + sort
      from?: string;
      to?: string;
      sort?: 'newest' | 'oldest';
    } = {}
  ) => {
    const res = await api.get('/user/all/admin', { params });
    return res.data; // { data: [...], pagination: {...} }
  }
);

export const fetchUsersAnalytics = createAsyncThunk(
  'users/fetchUsersAnalytics',
  async ({ userTimeRange }: { userTimeRange: 'daily' | 'weekly' | 'monthly' | 'yearly' }) => {
    const timeRange = userTimeRange;
    const res = await api.get('/user/analytics', { params: { timeRange } });
    return res.data;
  }
);

export const fetchTotalUsersAnalytics = createAsyncThunk(
  'users/fetchTotalUsersAnalytics',
  async () => {
    const res = await api.get('/user/total-users-analytics');
    return res.data;
  }
);

export const fetchPublisherAnalytics = createAsyncThunk(
  'users/fetchPublisherAnalytics',
  async ({ timeRange }: { timeRange: 'daily' | 'weekly' | 'monthly' | 'yearly' }) => {
    const res = await api.get('/user/publisher-analytics', { params: { timeRange } });
    return res.data;
  }
);

export const fetchAllUsers = createAsyncThunk(
  'users/fetchAllUsers',
  async (
    params: {
      page?: number;
      limit?: number;
    } = {}
  ) => {
    const res = await api.get('/user/all', { params });
    return res.data;
  }
);

export const assignRole = createAsyncThunk(
  'users/assignRole',
  async ({ userId, roleName }: { userId: string; roleName: string }) => {
    const res = await api.post(`/user/assign-role/${userId}`, { roleName });
    toast.success('Role assigned');
    return res.data;
  }
);

export const unassignRole = createAsyncThunk(
  'users/unassignRole',
  async ({ userId, roleName }: { userId: string; roleName: string }) => {
    const res = await api.post(`/user/unassign-role/${userId}`, { roleName });
    toast.success('Role unassigned');
    return res.data;
  }
);

export const addLatLong = createAsyncThunk(
  'users/addLatLong',
  async ({ latitude, longitude }: { latitude: number; longitude: number }) => {
    const res = await api.patch('/user/add-lat-long', { latitude, longitude });
    return res.data;
  }
);

export const setInactive = createAsyncThunk(
  'users/setInactive',
  async () => {
    const res = await api.patch('/user/inactive');
    return res.data;
  }
);

export const updateProfile = createAsyncThunk(
  'users/updateProfile',
  async (profileData: Record<string, any>) => {
    const res = await api.patch('/user', profileData);
    return res.data;
  }
);

export const updateProfilePic = createAsyncThunk(
  'users/updateProfilePic',
  async (formData: FormData) => {
    const res = await api.patch('/user/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  }
);

export const softDeleteUser = createAsyncThunk(
  'users/softDeleteUser',
  async (userId: string) => {
    console.log(userId);
    
    await api.patch(`/user/soft-delete/${userId}`);
    return userId;
  }
);

export const restoreUser = createAsyncThunk(
  'users/restoreUser',
  async (userId: string) => {
    const res = await api.patch(`/user/restore/${userId}`);
    return res.data;
  }
);

export const followUser = createAsyncThunk(
  'users/followUser',
  async (targetId: string) => {
    const res = await api.post(`/user/follow/${targetId}`);
    return res.data;
  }
);

export const unfollowUser = createAsyncThunk(
  'users/unfollowUser',
  async (targetId: string) => {
    await api.delete(`/user/un-follow/${targetId}`);
    return targetId;
  }
);

export const updatePassword = createAsyncThunk(
  'users/password',
  async (password: any, { rejectWithValue }) => {
    try {
      const res = await api.patch('/user/password', password.data);
      return res.data;
    } catch (err: any) {
      console.error("Error updating password:", err.response?.data || err.message);
      return rejectWithValue(err.response?.data || 'Unknown error');
    }
  }
);


// :contentReference[oaicite:1]{index=1}

// ————————————————————————————————
// State and Slice
// ————————————————————————————————

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

interface UserState {
  profile: any | null;
  userDetail: any | null;
  adminUserDetail: any | null;
  fullUser: any | null;
  adminList: any[];
  allList: any[];
  analytics: any | null;
  totalAnalytics: any | null;
  publisherAnalytics: any | null;
  adminPagination?: PaginationInfo;
  allPagination?: PaginationInfo;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: UserState = {
  profile: null,
  userDetail: null,
  adminUserDetail: null,
  fullUser: null,
  adminList: [],
  allList: [],
  analytics: null,
  totalAnalytics: null,
  publisherAnalytics: null,
  adminPagination: undefined,
  allPagination: undefined,
  status: 'idle',
  error: null,
};

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (b) => {
    b
      // fetchProfile
      .addCase(fetchProfile.pending, (s) => { s.status = 'loading'; })
      .addCase(fetchProfile.fulfilled, (s, a) => { s.profile = a.payload; s.status = 'succeeded'; })
      .addCase(fetchProfile.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message ?? null; })

      // fetchUserById
      .addCase(fetchUserById.fulfilled, (s, a) => { s.userDetail = a.payload; })

      // fetchUserByAdmin
      .addCase(fetchUserByAdmin.fulfilled, (s, a) => { s.adminUserDetail = a.payload; })

      // fetchFullUser
      .addCase(fetchFullUser.fulfilled, (s, a) => { s.fullUser = a.payload; })

      // fetchAllUsersAdmin
      .addCase(fetchAllUsersAdmin.pending, (s) => { s.status = 'loading'; })
      .addCase(fetchAllUsersAdmin.fulfilled, (s, a) => {
        s.adminList = a.payload.data;
        s.adminPagination = a.payload.pagination ?? null;
        s.status = 'succeeded';
      })
      .addCase(fetchAllUsersAdmin.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message ?? null; })

      // fetchUsersAnalytics
      .addCase(fetchUsersAnalytics.fulfilled, (s, a) => { s.analytics = a.payload; })

      // fetchTotalUsersAnalytics
      .addCase(fetchTotalUsersAnalytics.fulfilled, (s, a) => { s.totalAnalytics = a.payload; })

      // fetchPublisherAnalytics
      .addCase(fetchPublisherAnalytics.fulfilled, (s, a) => { s.publisherAnalytics = a.payload; })

      // fetchAllUsers
      .addCase(fetchAllUsers.pending, (s) => { s.status = 'loading'; })
      .addCase(fetchAllUsers.fulfilled, (s, a) => {
        s.allList = a.payload;
        s.allPagination = a.meta.arg?.page
          ? { currentPage: a.meta.arg.page, itemsPerPage: a.meta.arg.limit ?? 0, totalPages: Math.ceil(a.payload.length / (a.meta.arg.limit ?? a.payload.length)) }
          : undefined;
        s.status = 'succeeded';
      })
      .addCase(fetchAllUsers.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message ?? null; })

      // assignRole / unassignRole
      .addCase(assignRole.rejected, (s, a) => { s.error = a.error.message ?? null; toast.error('Failed to assign role'); })
      .addCase(unassignRole.rejected, (s, a) => { s.error = a.error.message ?? null; toast.error('Failed to unassign role'); })

      // addLatLong
      .addCase(addLatLong.fulfilled, (s, a) => { /* maybe update profile */ })

      // setInactive
      .addCase(setInactive.fulfilled, (s) => { /* update status in profile or list */ })

      // updateProfile
      .addCase(updateProfile.fulfilled, (s, a) => { s.profile = a.payload; toast.success('Profile updated'); })

      // updatePassword
      .addCase(updatePassword.fulfilled, (s, a) => { s.profile = a.payload; toast.success('Password updated'); })

      // updateProfilePic
      .addCase(updateProfilePic.fulfilled, (s, a) => { s.profile = a.payload; toast.success('Profile picture updated'); })

      // softDeleteUser
      .addCase(softDeleteUser.fulfilled, (s, a) => {
        s.adminList = s.adminList.filter(u => u.id !== a.payload);
      })

      // restoreUser
      .addCase(restoreUser.fulfilled, (s, a) => {
        // you might refetch or re-add the user
      })

      // follow / unfollow
      .addCase(followUser.fulfilled, (s, a) => { /* update follow status */ })
      .addCase(unfollowUser.fulfilled, (s, a) => { /* update follow status */ });
  }
});

export default userSlice.reducer;
